import FlagAvatar from '@app/src/components/Flags/FlagAvatar'
import FlagSuggestedRiskAvatar from '@app/src/components/Flags/FlagSuggestedRiskAvatar'
import AnswerWithFlagCell from '@app/src/components/Table/Cells/AnswerWithFlagCell'
import CompanyCell from '@app/src/components/Table/Cells/CompanyCell'
import IconCell from '@app/src/components/Table/Cells/IconCell'
import LinkCell from '@app/src/components/Table/Cells/LinkCell'
import ValueCell from '@app/src/components/Table/Cells/ValueCell'
import { AvatarSize } from '@app/src/components/Ui/Avatar'
import { useDialogState } from '@app/src/hooks/mui-hooks'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import { ReportFlagType } from '@app/src/types/flags'
import type { QuestionnaireTemplateSection, Response } from '@app/src/types/resourceExplorer'
import { WF_ORGANIZATION_ID } from '@app/src/wf-constants'
import paths from '@app/src/wf-constants/paths'
import { TableRow } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { generatePath } from 'react-router-dom'
import { dataHubAllowedFilters } from '..'
import DetailsFlagOverviewDialog from './DetailsFlagOverviewDialog'

type DataHubInsightsDetailsRowProps = {
  row: Response
  sections: QuestionnaireTemplateSection[]
  showTargetProductColumn?: boolean
}

const DataHubInsightsDetailsRow: React.FC<DataHubInsightsDetailsRowProps> = ({
  row: response,
  sections,
  showTargetProductColumn,
}) => {
  const { formatMessage } = useIntl()
  const [open, openDialog, closeDialog] = useDialogState(false)
  const filters = useGetApiQueryFilters(dataHubAllowedFilters)

  const questionsFilterIds = filters?.find(filter => filter.name === 'requestItem.questionId')?.filters?.[0]?.value
  const questionTypeFilterIds = filters?.find(filter => filter.name === 'requestItem.questionTypeId')?.filters?.[0]
    ?.value
  const isWorldfavorRule =
    response.items.find(i => i?.flagRule?.creatorOrganizationId === WF_ORGANIZATION_ID) !== undefined

  const flagRuleTemplateItemId = response.items
    .filter(item => item.flag !== ReportFlagType.NotSet && item.flagRule?.questionId)
    .map(item => item.flagRule?.questionId)

  const filteredSections = sections?.map(section => ({
    ...section,
    questions: section.questions
      .filter(question => flagRuleTemplateItemId.includes(question.id))
      .map(question => ({ ...question, isWorldfavorRule })),
  }))

  const responseItemsToShow =
    Array.isArray(questionsFilterIds) && questionsFilterIds.length
      ? response.items.filter(responseItem =>
          (questionsFilterIds as string[]).includes(String(responseItem.requestItem.questionId)),
        )
      : response.items

  const responseItemsToShowByType =
    Array.isArray(questionTypeFilterIds) && questionTypeFilterIds.length
      ? responseItemsToShow.filter(responseItem =>
          (questionTypeFilterIds as string[]).includes(String(responseItem.requestItem.questionTypeId)),
        )
      : responseItemsToShow

  const flagCount =
    Array.isArray(questionsFilterIds) && questionsFilterIds.length
      ? flagRuleTemplateItemId.filter(id => (questionsFilterIds as string[]).includes(String(id)))
      : flagRuleTemplateItemId

  return (
    <TableRow>
      <CompanyCell company={response?.request?.targetAliasObject} drawer maxRows={2} />
      {showTargetProductColumn &&
        (response.request.targetProduct ? (
          <LinkCell
            label={response.request.targetProduct?.name}
            to={generatePath(paths.product, { id: response.request.targetProduct?.id })}
          />
        ) : (
          <ValueCell />
        ))}
      <>
        <IconCell
          icon={
            isWorldfavorRule ? (
              <FlagSuggestedRiskAvatar
                size={AvatarSize.Small}
                isEnabled={Boolean(flagCount.length)}
                onClick={flagCount.length ? openDialog : undefined}
              />
            ) : (
              <FlagAvatar
                size={AvatarSize.Small}
                isEnabled={Boolean(flagCount.length)}
                onClick={flagCount.length ? openDialog : undefined}
              />
            )
          }
          title={String(flagCount.length)}
        />
        <DetailsFlagOverviewDialog
          open={open}
          title={formatMessage(
            { id: isWorldfavorRule ? 'statistics.suggestedRisksWithCount' : 'statistics.flaggedRisksWithCount' },
            { count: flagCount.length },
          )}
          onClose={closeDialog}
          sections={filteredSections}
        />
      </>
      {...responseItemsToShowByType.map(item => (
        <AnswerWithFlagCell
          item={item}
          key={item.id}
          maxRows={2}
          responderName={response?.request?.targetAliasObject.name}
        />
      ))}
    </TableRow>
  )
}

export default DataHubInsightsDetailsRow
